import {
  Socket,
  getEnvVar,
  SCREEN_PRESENTER,
  sendToDatadog,
  SOURCE_ROLE_SPEAKER,
  MEETING_ENDED_MODAL
} from '@shoflo/polymer';
import moment from 'moment';
import _ from 'underscore';

import {
  syncProgram,
  syncBanners,
  syncSources,
  deleteSource,
  syncOverlays,
  syncRecordings,
  syncBackgrounds,
  syncSceneLayers,
  setMixerAttendee,
  syncBannerStatus,
  syncOverlayStatus,
  syncOverlayPosition,
  syncBackgroundPosition,
  setAttendeeCameraStatus,
  setAttendeeConnectionMetric,
  setContentShareAudioProfile,
  muteMeetingAudio as muteMeetingAudioAction,
  updateSource,
  takePresenterLive,
  syncBroadcastSettings,
  createSandboxMeeting,
  setAttendeeDetails,
  setSceneLayout,
  cancelDisconnectSource,
  setMultipleAttendeeMuteStatuses,
  removeDesiredAttendeeCamera,
  getOrSyncScenes,
  setIsInitializing,
  setProgram
} from 'actions/Broadcast';

import {
  setAttendeeMuteStatus,
  setServerTimeDelta,
  clearModal,
  decreaseScreenShare,
  getCurrentBroadcast,
  getSources,
  requestReceived,
  clearDirectorRequest,
  showNotification,
  syncFeaturedPresenter,
  knockReceived,
  clearKnock,
  cancelKnock,
  toggleProgramSource,
  syncBrandingSettings,
  syncBackgroundStatus,
  setAttendeeCameraStatus as setAgoraAttendeeCameraStatus
} from '@shoflo/studio-redux/actions';

import { toggleLocalUserVideoTrack } from '@shoflo/studio-redux/utils';

import {
  KNOCKED,
  MUTE_CHANGED,
  BANNER_ADDED,
  OVERLAY_ADDED,
  SCENE_DELETED,
  SOURCE_TOGGLED,
  KNOCK_ANSWERED,
  BANNER_UPDATED,
  SOURCE_CREATED,
  BANNER_DELETED,
  OVERLAY_DELETED,
  BACKGROUND_ADDED,
  ENDPOINT_CREATED,
  ENDPOINT_DELETED,
  ENDPOINT_UPDATED,
  BACKGROUND_DELETED,
  PRESENTER_FEATURED,
  DIRECTOR_REQUESTED,
  BANNER_LIVE_TOGGLED,
  SCREEN_SHARE_STOPPED,
  AGORA_SCREEN_SHARE_STOPPED,
  SCENE_LAYERS_UPDATED,
  STREAM_STATE_TOGGLED,
  OVERLAY_LIVE_TOGGLED,
  SOURCE_ORDER_CHANGED,
  CAMERA_STATUS_CHANGED,
  BROADCAST_MIXER_JOINED,
  BACKGROUND_LIVE_TOGGLED,
  ATTENDEE_ROSTER_CHANGED,
  ATTENDEE_CAMERA_REMOVED,
  OVERLAY_POSITION_TOGGLED,
  DIRECTOR_REQUEST_ANSWERED,
  BROADCAST_PROGRAM_CHANGED,
  ATTENDEE_MIC_STATUS_CHANGED,
  BACKGROUND_POSITION_TOGGLED,
  BROADCAST_RECORDING_STARTED,
  BROADCAST_RECORDING_STOPPED,
  PRESENTER_AUDIO_BITRATE_CHANGED,
  ATTENDEE_CONNECTION_METRICS_CHANGED,
  BROADCAST_RECORDING_CREATED,
  ATTENDEE_LEFT,
  BROADCAST_RECORDING_UPDATED,
  BROADCAST_RECORDING_DELETED,
  REAL_TIME,
  MUTE_MEETING_AUDIO_CHANGED,
  SCENE_CREATED,
  SCENE_UPDATED,
  TOGGLED_PRESENTER_LIVE,
  AUTO_MUTE_TOGGLED,
  BROADCAST_JOIN_SYNCED_SERVER_TIME,
  KNOCK_UPDATED,
  INITIALIZED_READINESS_CHECK,
  SCENE_LAYOUT_CHANGED,
  CANCELLED_REQUESTS,
  KNOCK_REJECTED,
  KNOCK_FAILED,
  ATTENDEE_SCREEN_SHARE_CANCELLED,
  MULTIPLE_MUTE_CHANGED,
  MULTIPLE_ATTENDEE_MIC_STATUSES_CHANGED,
  TOGGLED_LAYOUT_ANIMATION,
  TOGGLED_ADD_SELF_LIVE,
  MULTIPLE_CAMERA_STATUSES_CHANGED,
  //RENEWED_MEETING,
  SYNCED_BRANDING_SETTINGS,
  SOURCE_DELETED,
  AGORA_ATTENDEE_MUTE_UNMUTE_CHANGED,
  AGORA_CAMERA_STATUS_CHANGED
} from 'store/eventConstants';

import isScenePreview from 'store/utils/isScenePreview';
import { getBroadcastRouteMatch } from 'actions/utils';
import { getCurrentUserId } from 'actions/Broadcast/utils';
import syncEndpoints from 'actions/Broadcast/stream/syncEndpoints';
import { UNASSIGNED } from 'utils/broadcastSourceHelper';
import {
  showWebSocketReconnectionMessage,
  showRealtimeErrorMessageModal
} from 'utils/realtimeSocketConnectionHelper';
import { LOST_REALTIME_SOCKET_CONNECTION } from 'constants/ModalTypes';
import { REALTIME_CONNECTION_TIMEOUT_VALUE } from '../constants';
import { getProgramLayers } from '../utils/broadcastSourceHelper';
import { processIncomingRealtimeMessage } from './socket-handlers';
import { BROADCAST_SCENE_SYNC } from 'actions/Types';
import {
  realtimeMuteLocalAudio,
  realtimeUnmuteLocalAudio,
  startLocalVideoTile,
  startVideoInput,
  stopContentShare,
  stopVideoInput
} from '@studio/chime';

import { muteLocalAudioStream } from '@cvent/studio-agora/audio/audioHelper';

import { MIXER_TASK_FAILED } from '@studio/constants';

/**
 * This class connects the redux store to the socket connection,
 * and manages joining / leaving event socket.io rooms as the event changes
 */
export default function () {
  this.connect = function () {
    // Create a new socket object
    this._socket = new Socket(getEnvVar('REACT_APP_SF_REALTIME_ENDPOINT'));
    return this._socket;
  };

  this.sameUser = userId => {
    const { user } = this._store.getState();
    if (userId) {
      return getCurrentUserId(user) === userId;
    }
    // this is to avoid loop conditions instances on socket events
    // where the user is not defined, which are historically many
    return true;
  };

  /*
    returns boolean if attendeeId is same sending director updates
  */
  this.sameAttendee = attendeeId => {
    const { meeting } = this._store.getState();
    const AttendeeId =
      meeting &&
      meeting.attendeeResponse &&
      meeting.attendeeResponse.Attendee.AttendeeId;
    return AttendeeId === attendeeId;
  };

  this.getEventIdFromRoute = () => {
    const { params } = getBroadcastRouteMatch(this._store.getState);
    return params.eventId;
  };

  this.hasRealTimeCollaborationAccess = () => {
    const { user } = this._store.getState();
    const eventId = this.getEventIdFromRoute();
    const userEvents = user?.events?.filter(evt => evt.event === eventId);
    const currentEvent = (userEvents && userEvents[0]) || Object.create(null);
    return currentEvent?.role !== 'readOnly';
  };

  this.isMeetingEndedModalOpen = () => {
    const { modal } = this._store.getState();
    return modal?.type === MEETING_ENDED_MODAL;
  };

  this.bindToStore = function (store) {
    this._store = store;
    const handleProgramChange = ({ program, notifyingDirector }) => {
      //const { meeting } = this._store.getState();
      //const { params } = getBroadcastRouteMatch(this._store.getState);
      // if its the scenePreview, ignore this
      if (!isScenePreview.call({ store })) {
        if (!this.sameAttendee(notifyingDirector)) {
          this._store.dispatch(syncProgram({ program, notifyingDirector }));
        }
      }
    };

    const handleCameraStatusChanged = async (
      isCameraOn,
      meetingSession,
      videoInputDevice
    ) => {
      if (isCameraOn) {
        await startVideoInput(meetingSession, videoInputDevice);
        await startLocalVideoTile(meetingSession);
      } else {
        // Turn Off Video
        await stopVideoInput(meetingSession);
      }
    };

    socketReconnected(this._socket, this._store);
    socketDisconnected(this._socket, this._store);
    socketConnected(this._socket, this._store);

    this.deletesourceByAttendeeId = attendeeId => {
      const { broadcastSources } = store.getState();
      const source = broadcastSources.sources.find(s => s.value === attendeeId);
      if (source) {
        store.dispatch(deleteSource(source._id));
      }
    };

    this.setSourceUnassignedByAttendeeId = (attendeeId, closedOrReloadTab) => {
      //we have a lambda to handle when the attendee is already in the meeting that is likely not working correctly
      //removed the code that filtered out sources that were not in a pending state.

      const { broadcastSources, program } = store.getState();
      const sources = broadcastSources.sources.filter(s =>
        [attendeeId, `${attendeeId}#content`].includes(s.value)
      );

      const layers = getProgramLayers(program);

      if (sources.length) {
        sources.forEach(source => {
          const [layerSource] = layers.filter(
            layer => layer.source._id === source._id
          );
          // Remove if source is in program
          if (!!layerSource) {
            store.dispatch(toggleProgramSource(source, setProgram));
          }
          store.dispatch(updateSource(source._id, { value: UNASSIGNED }));
          store.dispatch(removeDesiredAttendeeCamera(source.value));
          /** Resent initializing state of attendee source */
          store.dispatch(
            setIsInitializing(source.value, {
              isInitializing: false,
              isAttendee: true
            })
          );
          if (source.type === SCREEN_PRESENTER) {
            store.dispatch(decreaseScreenShare());
          }
        });

        if (closedOrReloadTab) {
          // We wait for chime to register that the attendee has left the session
          window.setTimeout(() => {
            sources.forEach(source => {
              store.dispatch(cancelDisconnectSource(source._id));
            });
          }, 3000);
        }
      }
    };
    // =================================== SOCKET EVENT LISTENERS ===================================//
    this._socket.on(BROADCAST_PROGRAM_CHANGED, handleProgramChange);

    this._socket.on(STREAM_STATE_TOGGLED, ({ notifyingDirector }) => {
      if (!this.sameAttendee(notifyingDirector)) {
        this._store.dispatch(syncEndpoints());
      }
    });

    this._socket.on(KNOCKED, payload => {
      if (
        this.hasRealTimeCollaborationAccess() &&
        !this.isMeetingEndedModalOpen()
      ) {
        this._store.dispatch(knockReceived(payload));
      }
    });

    this._socket.on(KNOCK_ANSWERED, ({ knockId, ...restPayload }) => {
      this._store.dispatch(clearKnock({ _id: knockId, ...restPayload }));
    });

    this._socket.on(KNOCK_REJECTED, ({ knockId, ...restPayload }) => {
      this._store.dispatch(clearKnock({ _id: knockId, ...restPayload }));
    });

    this._socket.on(KNOCK_FAILED, ({ knockId, ...restPayload }) => {
      this._store.dispatch(clearKnock({ _id: knockId, ...restPayload }));
    });

    this._socket.on(BROADCAST_MIXER_JOINED, ({ attendeeId }) => {
      store.dispatch(setMixerAttendee(attendeeId));
    });

    this._socket.on(MUTE_CHANGED, async ({ attendeeId, isMuted }) => {
      const {
        meeting: { attendeeResponse, meetingSession }
      } = store.getState();

      if (
        attendeeResponse &&
        attendeeResponse.Attendee.AttendeeId === attendeeId
      ) {
        if (isMuted) {
          await realtimeMuteLocalAudio(meetingSession);
        } else {
          await realtimeUnmuteLocalAudio(meetingSession);
        }
      }
    });

    this._socket.on(MULTIPLE_MUTE_CHANGED, async muteStatuses => {
      const {
        meeting: { attendeeResponse, meetingSession }
      } = store.getState();

      const attendeeId =
        attendeeResponse && attendeeResponse.Attendee.AttendeeId;
      const hasAttendeeId = attendeeId in muteStatuses;

      if (!hasAttendeeId) {
        return;
      }

      if (muteStatuses[attendeeId]) {
        await realtimeMuteLocalAudio(meetingSession);
      } else {
        await realtimeUnmuteLocalAudio(meetingSession);
      }
    });

    this._socket.on(
      AGORA_ATTENDEE_MUTE_UNMUTE_CHANGED,
      async ({ attendeeId, isMuted }) => {
        const { meeting, user } = this._store.getState();
        const localAttendeeId = `user-${user._id}`;

        const localAudioTrack = meeting.client.localTracks.find(
          ({ trackMediaType }) => trackMediaType === 'audio'
        );

        if (attendeeId === localAttendeeId && localAudioTrack) {
          await muteLocalAudioStream(localAudioTrack, isMuted);
        }

        this._store.dispatch(setAttendeeMuteStatus(attendeeId, isMuted));
      }
    );

    this._socket.on(
      AGORA_CAMERA_STATUS_CHANGED,
      async ({ attendeeId, isCameraOn }) => {
        const {
          meeting: { attendeeTiles, client },
          user
        } = this._store.getState();
        const localAttendeeId = `user-${user._id}`;
        const localVideoTrack = attendeeTiles[localAttendeeId];
        const isLocalUser = attendeeId === localAttendeeId;

        if (isLocalUser) {
          await toggleLocalUserVideoTrack({
            currentVideoTrack: localVideoTrack,
            isCameraOn,
            client,
            dispatch: this._store.dispatch,
            attendeeId
          });
        }

        this._store.dispatch(
          setAgoraAttendeeCameraStatus(attendeeId, isCameraOn, setProgram)
        );
      }
    );

    this._socket.on(
      ATTENDEE_CONNECTION_METRICS_CHANGED,
      ({ attendeeId, metrics }) => {
        // TODO: throttle this?
        store.dispatch(setAttendeeConnectionMetric(attendeeId, metrics));
      }
    );

    this._socket.on(
      CAMERA_STATUS_CHANGED,
      async ({ attendeeId, isCameraOn }) => {
        const {
          meeting: { attendeeResponse, meetingSession },
          videoInputDevice
        } = store.getState();

        if (
          attendeeResponse &&
          attendeeResponse.Attendee.AttendeeId === attendeeId
        ) {
          await handleCameraStatusChanged(
            isCameraOn,
            meetingSession,
            videoInputDevice
          );
        }
        store.dispatch(setAttendeeCameraStatus(attendeeId, isCameraOn));
      }
    );

    /*
    By Default AWS chime uses the same method to determine when a video tile is removed and when it disconnects.
    Adding a socket here to handle when the camera is removed ensures that when the video stream diconnects,
    it doesnt flicker on the screen. it tries to reconnects and the stream continues if possible.
    */
    this._socket.on(
      ATTENDEE_CAMERA_REMOVED,
      async ({ attendeeId, isCameraOn }) => {
        store.dispatch(setAttendeeCameraStatus(attendeeId, isCameraOn));
      }
    );

    /**
     * This Socket handles when the presenter requests for director permission to share resource.
     */
    this._socket.on(DIRECTOR_REQUESTED, async payload => {
      if (this.hasRealTimeCollaborationAccess()) {
        store.dispatch(requestReceived(payload));
      }
    });

    this._socket.on(DIRECTOR_REQUEST_ANSWERED, payload => {
      if (this.hasRealTimeCollaborationAccess()) {
        this._store.dispatch(clearDirectorRequest(payload));
      }
    });

    this._socket.on(SOURCE_TOGGLED, async ({ notifyingDirector }) => {
      if (!this.sameAttendee(notifyingDirector)) {
        store.dispatch(getSources());
      }
    });

    this._socket.on(SOURCE_CREATED, async ({ notifyingDirector }) => {
      if (!this.sameAttendee(notifyingDirector)) {
        store.dispatch(getSources());
      }
    });

    this._socket.on(SOURCE_DELETED, async ({ notifyingDirector }) => {
      if (!this.sameAttendee(notifyingDirector)) {
        store.dispatch(getSources());
      }
    });

    this._socket.on(
      ATTENDEE_ROSTER_CHANGED,
      async ({ notifyingDirector, attendeeRoster }) => {
        const { attendeeRoster: localAttendeeRoster } = this._store.getState();

        if (notifyingDirector && !this.sameAttendee(notifyingDirector)) {
          if (!_.isEqual(attendeeRoster, localAttendeeRoster)) {
            store.dispatch(getSources());
          }
        }
      }
    );

    this._socket.on(
      ATTENDEE_MIC_STATUS_CHANGED,
      async ({ attendeeId, isMuted }) => {
        store.dispatch(setAttendeeMuteStatus(attendeeId, isMuted));
      }
    );

    this._socket.on(
      MULTIPLE_ATTENDEE_MIC_STATUSES_CHANGED,
      async muteStatuses => {
        store.dispatch(setMultipleAttendeeMuteStatuses(muteStatuses));
      }
    );

    // When director changes audio bitrate for an attendee
    this._socket.on(
      PRESENTER_AUDIO_BITRATE_CHANGED,
      async ({ attendeeId, contentShareAudioProfile }) => {
        const {
          meeting: { attendeeResponse }
        } = store.getState();
        if (
          attendeeResponse &&
          attendeeResponse.Attendee.AttendeeId === attendeeId
        ) {
          if (contentShareAudioProfile) {
            store.dispatch(setContentShareAudioProfile());
          }
        }
      }
    );

    this._socket.on(SCREEN_SHARE_STOPPED, async ({ attendeeId }) => {
      const {
        meeting: { attendeeResponse, meetingSession }
      } = store.getState();

      // If the local attendee matches the attendee who's Screenshare we want to stop, we fire this action
      if (
        attendeeResponse &&
        attendeeResponse.Attendee.AttendeeId === attendeeId
      ) {
        // end screen share
        await stopContentShare(meetingSession);
        store.dispatch(decreaseScreenShare());
      }
    });

    this._socket.on(AGORA_SCREEN_SHARE_STOPPED, async ({ attendeeId }) => {
      const {
        meeting: { screenShare, attendeeId: localAttendeeId }
      } = store.getState();
      const { client, videoTrack, audioTrack } = screenShare;

      const localScreenShareId = `${localAttendeeId}#content`;

      if (localScreenShareId === attendeeId) {
        const { stopScreenShare } = await import(
          '@cvent/studio-agora/screenShare/screenShareHelper'
        );
        await stopScreenShare(client, videoTrack, audioTrack);
      }
      this.setSourceUnassignedByAttendeeId(attendeeId);
      store.dispatch(decreaseScreenShare());
    });

    this._socket.on(OVERLAY_ADDED, async () => {
      store.dispatch(syncOverlays());
    });

    this._socket.on(OVERLAY_DELETED, async () => {
      store.dispatch(syncOverlays());
    });

    this._socket.on(BACKGROUND_ADDED, async () => {
      store.dispatch(syncBackgrounds());
    });

    this._socket.on(BACKGROUND_DELETED, async () => {
      store.dispatch(syncBackgrounds());
    });

    this._socket.on(BANNER_ADDED, async () => {
      store.dispatch(syncBanners());
    });

    this._socket.on(BANNER_DELETED, async () => {
      store.dispatch(syncBanners());
    });

    this._socket.on(
      OVERLAY_LIVE_TOGGLED,
      async ({ overlayId, notifyingDirector }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncOverlayStatus(overlayId));
        }
      }
    );

    this._socket.on(
      BACKGROUND_LIVE_TOGGLED,
      async ({ backgroundId, notifyingDirector }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncBackgroundStatus(backgroundId));
        }
      }
    );

    this._socket.on(
      OVERLAY_POSITION_TOGGLED,
      async ({ overlayId, notifyingDirector }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncOverlayPosition(overlayId));
        }
      }
    );

    this._socket.on(
      BACKGROUND_POSITION_TOGGLED,
      async ({ backgroundId, notifyingDirector }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncBackgroundPosition(backgroundId));
        }
      }
    );

    this._socket.on(
      BANNER_LIVE_TOGGLED,
      async ({ bannerId, notifyingDirector }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncBannerStatus(bannerId));
        }
      }
    );

    this._socket.on(BANNER_UPDATED, async ({ notifyingDirector }) => {
      if (!this.sameAttendee(notifyingDirector)) {
        store.dispatch(syncBanners());
      }
    });

    this._socket.on(
      SCENE_LAYERS_UPDATED,
      async ({ notifyingDirector, sceneLayers }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncSceneLayers(sceneLayers));
        }
      }
    );

    this._socket.on(ENDPOINT_CREATED, async () => {
      store.dispatch(syncEndpoints());
    });

    this._socket.on(ENDPOINT_DELETED, async () => {
      store.dispatch(syncEndpoints());
    });

    this._socket.on(ENDPOINT_UPDATED, async () => {
      store.dispatch(syncEndpoints());
    });

    this._socket.on(
      PRESENTER_FEATURED,
      async ({ notifyingDirector, presenterId }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncFeaturedPresenter(presenterId));
        }
      }
    );

    this._socket.on(
      SOURCE_ORDER_CHANGED,
      async ({ presenterId, isPresenterSource, notifyingDirector }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          if (isPresenterSource) {
            store.dispatch(syncFeaturedPresenter(presenterId));
          } else {
            store.dispatch(syncSources());
          }
        }
      }
    );

    this._socket.on(
      AUTO_MUTE_TOGGLED,
      async ({ autoMutePresenters, notifyingDirector }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncBroadcastSettings({ autoMutePresenters }));
        }
      }
    );

    /**
     * This Socket handles when the presenter is leaving the meeting.
     */
    this._socket.on(
      ATTENDEE_LEFT,
      async ({ attendeeId, closedOrReloadTab, isPresenterDismissed }) => {
        if (!isPresenterDismissed) {
          this.setSourceUnassignedByAttendeeId(attendeeId, closedOrReloadTab);
        }
      }
    );

    this._socket.on(BROADCAST_RECORDING_STARTED, async () => {
      store.dispatch(syncRecordings());
    });

    this._socket.on(BROADCAST_RECORDING_STOPPED, async () => {
      store.dispatch(syncRecordings());
    });

    this._socket.on(BROADCAST_RECORDING_CREATED, async () => {
      store.dispatch(syncRecordings());
    });

    this._socket.on(BROADCAST_RECORDING_UPDATED, async () => {
      store.dispatch(syncRecordings());
    });

    this._socket.on(BROADCAST_RECORDING_DELETED, async () => {
      store.dispatch(syncRecordings());
    });

    this._socket.on(
      MUTE_MEETING_AUDIO_CHANGED,
      async ({ muteMeetingAudio, notifyingDirector }) => {
        const { broadcastSettings } = store.getState();
        if (!this.sameAttendee(notifyingDirector)) {
          if (
            broadcastSettings &&
            broadcastSettings.muteMeetingAudio !== muteMeetingAudio
          ) {
            store.dispatch(muteMeetingAudioAction({ muteMeetingAudio }));
          }
        }
      }
    );

    this._socket.on(REAL_TIME, async payload => {
      processIncomingRealtimeMessage.call({ store, payload });
    });

    this._socket.on(SCENE_DELETED, async ({ notifyingDirector }) => {
      if (!this.sameAttendee(notifyingDirector)) {
        store.dispatch(getOrSyncScenes(BROADCAST_SCENE_SYNC));
      }
    });

    this._socket.on(SCENE_CREATED, async ({ notifyingDirector }) => {
      if (!this.sameAttendee(notifyingDirector)) {
        store.dispatch(getOrSyncScenes(BROADCAST_SCENE_SYNC));
      }
    });

    this._socket.on(
      TOGGLED_LAYOUT_ANIMATION,
      async ({ notifyingDirector, layoutAnimations }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncBroadcastSettings({ layoutAnimations }));
        }
      }
    );

    this._socket.on(
      TOGGLED_ADD_SELF_LIVE,
      async ({ notifyingDirector, addSelfLive }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(syncBroadcastSettings({ addSelfLive }));
        }
      }
    );

    this._socket.on(SCENE_UPDATED, ({ notifyingDirector }) => {
      if (!this.sameAttendee(notifyingDirector)) {
        store.dispatch(getOrSyncScenes(BROADCAST_SCENE_SYNC));
      }
    });

    this._socket.on(
      SCENE_LAYOUT_CHANGED,
      async ({ layout, notifyingDirector }) => {
        if (!this.sameAttendee(notifyingDirector)) {
          store.dispatch(setSceneLayout(layout));
        }
      }
    );

    this._socket.on(TOGGLED_PRESENTER_LIVE, ({ attendeeId }) => {
      const { broadcastSources } = store.getState();
      const source = broadcastSources.sources.find(s => s.value === attendeeId);

      if (!!source) {
        store.dispatch(takePresenterLive(source));
      }
    });

    // Used to sync director time to real-time server time when director first loads up

    this._socket.on(
      BROADCAST_JOIN_SYNCED_SERVER_TIME,
      async ({ serverTime }) => {
        const directorTime = moment().utc().unix();
        const now = moment.unix(directorTime);
        const then = moment.unix(serverTime);
        const serverTimeDelta = now.diff(then, 'seconds'); // Get seconds difference
        store.dispatch(setServerTimeDelta(serverTimeDelta));
      }
    );

    this._socket.on(
      KNOCK_UPDATED,
      ({
        attendeeId,
        name,
        description,
        presenterPhoto,
        userId,
        cameraUnavailable
      }) => {
        /**
         * If presenter is joining the meeting, we set details in state first as a way of
         * passing them to `setAttendeePresence` action which will create a source.
         */
        this._store.dispatch(
          setAttendeeDetails(
            attendeeId,
            name,
            description,
            presenterPhoto,
            SOURCE_ROLE_SPEAKER
          )
        );

        const { broadcastSources } = store.getState();

        const presenter = broadcastSources.sources.find(
          ({ value }) => value === attendeeId
        );

        /**
         * If presenter updates their details while already in the meeting and we have
         * them as a source, we just update that source.
         */
        if (presenter) {
          const payload = {
            title: name,
            description,
            userId,
            role: SOURCE_ROLE_SPEAKER,
            cameraUnavailable
          };

          this._store.dispatch(updateSource(presenter._id, payload));
        }
      }
    );

    this._socket.on(INITIALIZED_READINESS_CHECK, payload => {
      if (!!payload) {
        store.dispatch(createSandboxMeeting());
      }
    });

    this._socket.on(CANCELLED_REQUESTS, payload => {
      this._store.dispatch(cancelKnock(payload));
    });

    this._socket.on(ATTENDEE_SCREEN_SHARE_CANCELLED, attendeeId => {
      const { broadcastSources } = store.getState();
      store.dispatch(decreaseScreenShare());
      const { sources } = broadcastSources;

      const sourceValue = `${attendeeId}#content`;

      const source = sources
        .filter(({ type }) => type === SCREEN_PRESENTER)
        .find(({ value }) => value === sourceValue);
      /**
       * Source will be undefined if director approves screenshare as a new source.
       * This will only have value if screenshare was mapped to a placeholder.
       */
      if (source) {
        store.dispatch(
          updateSource(source._id, {
            value: UNASSIGNED
          })
        );
        store.dispatch(
          setIsInitializing(source.value, {
            isInitializing: false,
            isAttendee: true
          })
        );
      }

      const screenShareName = source?.title || 'Screen share';
      const message = `${screenShareName} was cancelled by presenter`;

      store.dispatch(
        showNotification(message, {
          timeout: 5500,
          type: 'alert'
        })
      );
    });

    this._socket.on(
      MULTIPLE_CAMERA_STATUSES_CHANGED,
      async ({ attendeeCameraStatuses }) => {
        const {
          meeting: { attendeeResponse, meetingSession },
          videoInputDevice
        } = store.getState();

        const attendeeId =
          attendeeResponse && attendeeResponse.Attendee.AttendeeId;

        const isCurrentAttendee =
          typeof attendeeCameraStatuses[attendeeId] === 'boolean';

        if (!isCurrentAttendee) {
          return;
        }

        const isCameraOn = attendeeCameraStatuses[attendeeId];

        await handleCameraStatusChanged(
          isCameraOn,
          meetingSession,
          videoInputDevice
        );

        store.dispatch(setAttendeeCameraStatus(attendeeId, isCameraOn));
      }
    );

    this._socket.on(SYNCED_BRANDING_SETTINGS, async ({ brandingSettings }) => {
      store.dispatch(syncBrandingSettings(brandingSettings));
    });

    this._socket.on(MIXER_TASK_FAILED, async ({ mixerTaskType }) => {
      const message = `We found an issue with a ${mixerTaskType}. Try restarting it for the best experience.`;
      store.dispatch(
        showNotification(message, {
          timeout: 5000,
          type: 'alert'
        })
      );
    });
  };
}

/**
 *
 * @param {*} socket
 * @param {*} store
 */
const socketConnected = (socket, store) => {
  socket.on('connect', () => {
    store.dispatch(clearModal(LOST_REALTIME_SOCKET_CONNECTION));
  });

  const {
    meeting: { attendeeResponse }
  } = store.getState();
  const payload = {
    attendeeId: attendeeResponse?.Attendee?.AttendeeId ?? 'unavailable'
  };
  sendToDatadog('socket-dispatcher', payload, 'Realtime socket connected');
};

/**
 *
 * This function dispatches an event to get the most recent data from the api
 *
 * @param {Object} socket reference
 * @param {Object} store reference
 */
const socketReconnected = (socket, store) => {
  const socketIOSocket = socket.eject();
  socketIOSocket.io.on('reconnect', () => {
    // This was done to align pro/lite code
    // This will be moved to the socket package in the ticket below
    // https://jira.cvent.com/browse/WEBINAR-28554
    reconnectRealTimeMessage(store.dispatch, store.getState);
  });
};

function reconnectRealTimeMessage(dispatch, getState) {
  const {
    meeting: { attendeeResponse }
  } = getState();

  showWebSocketReconnectionMessage(dispatch);

  window.realtimeConnectionLostTimeout = null;
  dispatch(getCurrentBroadcast(true));

  const payload = {
    attendeeId: attendeeResponse?.Attendee?.AttendeeId ?? 'unavailable'
  };

  sendToDatadog(
    'socket-dispatcher',
    payload,
    'Realtime connection restored',
    true
  );
}

const socketDisconnected = (socket, store) => {
  const socketIOSocket = socket.eject();
  socketIOSocket.on('disconnect', () => {
    const {
      meeting: { attendeeResponse },
      modal
    } = store.getState();

    // If the meeting has ended byt chime with no intialValue then do not show RealtimeErrorMessage modal
    const hasMeetingEnded =
      modal?.type === MEETING_ENDED_MODAL && !modal?.initialValue;

    if (!window.realtimeConnectionLostTimeout && !hasMeetingEnded) {
      window.realtimeConnectionLostTimeout = setTimeout(() => {
        showRealtimeErrorMessageModal(store.dispatch);
      }, REALTIME_CONNECTION_TIMEOUT_VALUE);
    }
    const payload = {
      attendeeId: attendeeResponse?.Attendee?.AttendeeId ?? 'unavailable'
    };
    sendToDatadog(
      'socket-dispatcher',
      payload,
      'Realtime connection lost',
      true
    );
  });
};
